import { FC, useCallback, useContext, useMemo } from 'react'

import { StackSeparator, Text, VStack } from '@chakra-ui/react'
import { ToolbarMoveIcon } from '@editor/assets/icons'
import { EDITOR_TOOLS, EDITOR_TOOLS_LABELS, EDITOR_TOOL_BUTTON_SIZE, EditorToolKey } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import { EditorTool, ToolbarCategory } from '@editor/interfaces/editor'
import { hashCode } from '@editor/services/Util'
import { getVolumeEstimationItem } from '@editor/services/VolumeEstimation'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { Tools, getCurrentEditorTool } from '../tools/setup'
import HelpButton from './HelpButton'
import ToolbarButton from './components/ToolbarButton'

const Toolbar: FC = () => {
  // Context
  const { selectedTool, changeTool } = useContext(EditorContext)

  // Store
  const userProfile = useSelector((state: RootState) => state.user.userProfile)
  const userType = useSelector((state: RootState) => state.user.userType)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)

  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)

  const isJobRunning = useSelector((state: RootState) => state.editor.isJobRunning)
  const jobRunningTool = useSelector((state: RootState) => state.editor.jobRunningTool)
  const disableTools = useSelector((state: RootState) => state.toolUtil.core.disableTools)

  // Vars
  const volumeInspectionItem = useMemo(() => getVolumeEstimationItem(inspectionItems), [inspectionItems])
  const hasCylinders = useMemo(() => shapes.cylinders?.length > 0, [shapes])
  const isAllowedToModify = useMemo(() => permissionSet.MODIFY.includes(userType), [permissionSet, userType])

  // Group tools by category
  const groupedTools = useMemo(() => {
    const categories = Tools.reduce<{ category: ToolbarCategory; tools: EditorTool[] }[]>((collection, tool) => {
      const category = tool.toolbar?.category || ToolbarCategory.None
      const groupIndex = category as number
      const group = collection[groupIndex] || { category, tools: [] }

      if (userProfile && tool.toolbar && tool.authCheck(userProfile, isAllowedToModify)) {
        group.tools.push(tool)
      }

      if (!collection[groupIndex]) {
        collection[groupIndex] = group
      }

      return collection
    }, [])

    return categories
  }, [userProfile, isAllowedToModify])

  const getWarningLabel = useCallback(
    (tool: EditorTool) => {
      const msgs = []

      if (tool?.config?.volume?.required && !volumeInspectionItem) {
        msgs.push('️体積測定を先に実施してください')
      }

      if (tool?.config?.cylinder?.required && !hasCylinders) {
        msgs.push('先に鉄筋を検出してください')
      }

      if (!msgs.length) return undefined

      return (
        <>
          {msgs.map((s) => (
            <Text key={hashCode(s)}>{s}</Text>
          ))}
        </>
      )
    },
    [volumeInspectionItem, hasCylinders],
  )

  /**
   * Check if the tool is locked based on the current job status and dependencies.
   */
  const isLocked = useCallback(
    (tool: EditorToolKey) => {
      if (disableTools) return true
      if (!isJobRunning) return false

      const deps = getCurrentEditorTool(jobRunningTool)?.config?.dependencies
      if (deps !== undefined) return deps.includes(tool)

      return false
    },
    [disableTools, isJobRunning, jobRunningTool],
  )

  return (
    <VStack left={2} overflow="hidden" position="absolute" top={2} w={EDITOR_TOOL_BUTTON_SIZE} gap={2} id="toolbar">
      <VStack
        backgroundColor="gray2.800"
        overflow="hidden"
        borderRadius="md"
        separator={<StackSeparator borderColor="whiteAlpha.200" />}
        gap={0}
        w={EDITOR_TOOL_BUTTON_SIZE}
      >
        <ToolbarButton
          icon={<ToolbarMoveIcon width="50%" height="50%" />}
          label={EDITOR_TOOLS_LABELS.MOVE}
          toolType={EDITOR_TOOLS.MOVE}
          buttonSize={EDITOR_TOOL_BUTTON_SIZE}
          selectedTool={selectedTool}
          changeTool={changeTool}
        />
      </VStack>
      {groupedTools.map((group) =>
        group.tools.length ? (
          <VStack
            key={group.category}
            data-testid={`ToolbarGroup-${Object.values(ToolbarCategory)[group.category]}`}
            backgroundColor="gray2.800"
            overflow="hidden"
            borderRadius="md"
            separator={<StackSeparator borderColor="whiteAlpha.200" />}
            gap={0}
            w={EDITOR_TOOL_BUTTON_SIZE}
          >
            {group.tools.map((tool) => (
              <ToolbarButton
                key={tool.key}
                icon={tool.toolbar!.icon}
                label={tool.toolbar!.label}
                warningLabel={getWarningLabel(tool)}
                disabled={
                  (tool?.config?.volume?.required && !volumeInspectionItem) ||
                  (tool?.config?.cylinder?.required && !hasCylinders) ||
                  isLocked(tool.key)
                }
                toolType={tool.key}
                buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                selectedTool={selectedTool}
                changeTool={changeTool}
                variants={tool.toolbar?.variants?.reduce<
                  {
                    icon: React.ReactElement
                    toolType: string
                    label: string
                  }[]
                >((collection, variant) => {
                  if (variant.toolbar && variant.authCheck(userProfile, isAllowedToModify)) {
                    collection.push({
                      toolType: variant.key,
                      icon: variant.toolbar.icon,
                      label: variant.toolbar.label,
                    })
                  }

                  return collection
                }, [])}
              />
            ))}
          </VStack>
        ) : null,
      )}
      <VStack
        backgroundColor="gray2.800"
        overflow="hidden"
        borderRadius="md"
        separator={<StackSeparator borderColor="whiteAlpha.200" />}
        gap={0}
        w={EDITOR_TOOL_BUTTON_SIZE}
      >
        <HelpButton />
      </VStack>
    </VStack>
  )
}

export default Toolbar
